import React, { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { useAuth, isAdmin } from "../context/AuthContext";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import ProductHierarchyBoard from "../components/ProductHierarchyBoard";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const SIMPLE_KINDS = [
  { key: "crops", label: "Crops", hint: "1509, 1718, Sharbati…" },
  { key: "rice-types", label: "Rice types", hint: "Steam, White Sella…" },
];

function SimpleMasterTab({ kind, label, hint }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ code: "", name: "", sort_order: "0" });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", sort_order: "0" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/production/masters/${kind}`, { params: { include_inactive: 1 } });
      setRows(data.results || []);
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/production/masters/${kind}`, {
        code: form.code,
        name: form.name,
        sort_order: Number(form.sort_order) || 0,
      });
      toast.success(`${label.slice(0, -1)} added`);
      setForm({ code: "", name: "", sort_order: "0" });
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const saveEdit = async (id) => {
    try {
      await api.patch(`/production/masters/${kind}/${id}`, {
        name: editForm.name,
        sort_order: Number(editForm.sort_order) || 0,
      });
      toast.success("Updated");
      setEditingId(null);
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  const toggleActive = async (row) => {
    try {
      await api.patch(`/production/masters/${kind}/${row.id}`, { is_active: !row.is_active });
      toast.success(row.is_active ? "Deactivated" : "Restored");
      load();
    } catch (err) {
      toast.error(extractError(err));
    }
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">{hint}</p>
      <form onSubmit={add} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Code</Label>
          <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Name</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="mt-1" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Sort</Label>
          <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1" />
        </div>
        <Button type="submit" className="bg-bansal-sage hover:bg-bansal-sageDark text-white min-h-10">
          <Plus className="h-4 w-4 mr-1" /> Add
        </Button>
      </form>

      {loading ? (
        <p className="text-sm text-muted-foreground py-6 text-center">Loading…</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-border">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-[10px] uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Name</th>
                <th className="text-left px-3 py-2">Sort</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">No rows yet</td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-t border-border/60">
                    <td className="px-3 py-2 font-mono text-xs">{row.code}</td>
                    <td className="px-3 py-2">
                      {editingId === row.id ? (
                        <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-8" />
                      ) : (
                        row.name
                      )}
                    </td>
                    <td className="px-3 py-2 tabular">
                      {editingId === row.id ? (
                        <Input type="number" value={editForm.sort_order} onChange={(e) => setEditForm({ ...editForm, sort_order: e.target.value })} className="h-8 w-20" />
                      ) : (
                        row.sort_order
                      )}
                    </td>
                    <td className="px-3 py-2">
                      <Pill tone={row.is_active ? "sage" : "muted"}>{row.is_active ? "Active" : "Inactive"}</Pill>
                    </td>
                    <td className="px-3 py-2 text-right space-x-2">
                      {editingId === row.id ? (
                        <>
                          <Button type="button" size="sm" variant="outline" onClick={() => saveEdit(row.id)}>Save</Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                        </>
                      ) : (
                        <>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              setEditingId(row.id);
                              setEditForm({ name: row.name, sort_order: String(row.sort_order ?? 0) });
                            }}
                          >
                            Edit
                          </Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => toggleActive(row)}>
                            {row.is_active ? "Deactivate" : "Restore"}
                          </Button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ProductionBatchMastersPage() {
  const { user } = useAuth();
  if (!isAdmin(user)) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="batch-masters-page">
      <PageHeader
        eyebrow="Administration"
        title="Batch classification masters"
        description="Crops, rice types, and the Product → Group → Item hierarchy used by packing and yield extract."
      />
      <SectionCard title="Masters" className="overflow-visible">
        <Tabs defaultValue="hierarchy">
          <TabsList className="mb-5 flex flex-wrap h-auto gap-1">
            <TabsTrigger value="hierarchy">Architecture matrix</TabsTrigger>
            {SIMPLE_KINDS.map((k) => (
              <TabsTrigger key={k.key} value={k.key}>{k.label}</TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="hierarchy" className="mt-0">
            <ProductHierarchyBoard />
          </TabsContent>
          {SIMPLE_KINDS.map((k) => (
            <TabsContent key={k.key} value={k.key}>
              <SimpleMasterTab kind={k.key} label={k.label} hint={k.hint} />
            </TabsContent>
          ))}
        </Tabs>
      </SectionCard>
    </div>
  );
}
